import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Eye, FileText, MessageSquare, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, DOC_META, STATUS_META, fileUrl } from "@/lib/api";

export default function DocumentVerificationModal({ open, onOpenChange, student, onUpdated }) {
  const [activeType, setActiveType] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  if (!student) return null;
  const docs = student.documents || {};

  const validate = async (type, valid) => {
    setLoading(true);
    try {
      await api.put(`/admin/students/${student.id}/validate-document/${type}`, { valid, note });
      toast.success(valid ? `${DOC_META[type].label} diverifikasi` : `Permintaan perbaikan dikirim`);
      setActiveType(null);
      setNote("");
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="verification-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Verifikasi Dokumen Siswa</DialogTitle>
          <DialogDescription>Periksa setiap dokumen, lalu tandai Lengkap atau minta perbaikan dengan catatan.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {Object.entries(DOC_META).map(([type, meta]) => {
            const doc = docs[type];
            const status = doc?.status || "draft";
            const smeta = STATUS_META[status] || STATUS_META.draft;
            const isActive = activeType === type;
            return (
              <div key={type} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`verify-doc-${type}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-white">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-500" />
                      <span className="font-semibold text-slate-900">{meta.label}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${smeta.cls}`}>
                        <span className={`w-1 h-1 rounded-full ${smeta.dot}`} />
                        {doc?.status === "verified" ? "Lengkap" : smeta.label}
                      </span>
                    </div>
                    {doc ? (
                      <div className="text-xs text-slate-500 mt-1">
                        {doc.filename} • {(doc.size / 1024).toFixed(1)} KB • {new Date(doc.uploaded_at).toLocaleDateString("id-ID")}
                      </div>
                    ) : (
                      <div className="text-xs text-slate-400 italic mt-1">Belum diunggah siswa</div>
                    )}
                    {doc?.note && doc.status === "needs_fix" && <div className="text-xs text-rose-700 mt-1">Catatan: {doc.note}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    {doc && (
                      <a
                        href={fileUrl(doc.path)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-medium text-slate-700 hover:bg-slate-50"
                        data-testid={`view-icon-${type}`}
                      >
                        <Eye className="w-3.5 h-3.5" /> Lihat
                      </a>
                    )}
                    {doc && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setActiveType(isActive ? null : type); setNote(doc.note || ""); }}
                        data-testid={`toggle-verify-${type}`}
                      >
                        {isActive ? "Tutup" : "Verifikasi"}
                      </Button>
                    )}
                  </div>
                </div>

                {isActive && doc && (
                  <div className="p-4 bg-slate-50 border-t border-slate-200 space-y-3">
                    <div>
                      <label className="text-xs font-medium text-slate-700 flex items-center gap-1 mb-1.5">
                        <MessageSquare className="w-3.5 h-3.5" /> Catatan (opsional)
                      </label>
                      <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Contoh: KK tidak jelas, mohon di-scan ulang..." data-testid={`note-input-${type}`} />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => validate(type, true)}
                        disabled={loading}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`validate-doc-${type}`}
                      >
                        {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                        Validasi & Verifikasi
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => validate(type, false)}
                        disabled={loading}
                        className="border-rose-300 text-rose-700 hover:bg-rose-50"
                        data-testid={`reject-doc-${type}`}
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Minta Perbaikan
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
